/**
 * Tenant resolver (Phase 13 Loop 1).
 *
 * On SPA boot, calls GET /v1/tenant/resolve?host=<hostname> and caches
 * the result in sessionStorage for the rest of the session. The result
 * drives per-tenant branding (CSS variable overrides), the
 * "Powered by NEXIRT" footer toggle (entitlement-gated), and the
 * tenant-scoped Organization JSON-LD on tenant subdomains.
 *
 * On nexirt.com (the marketing apex) this always returns the synthetic
 * "nexirt-internal" tenant — no network round-trip, no subdomain match.
 */

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.nexirt.com";
const TENANT_ROOT = "app.nexirt.com";

export type TenantBranding = {
  logoUrl?: string;
  accentColor?: string;
  accentColorSecondary?: string;
  customCSS?: Record<string, string>;
};

export type TenantResolved = {
  tenantId: string;
  name: string;
  branding: TenantBranding | null;
  status: "active" | "suspended" | "cancelled";
};

const CACHE_KEY = "nexirt.tenant.resolved.v1";
let inflight: Promise<TenantResolved> | null = null;

export function currentHostname(): string {
  return typeof window === "undefined" ? "" : window.location.hostname.toLowerCase();
}

export function isTenantSubdomain(hostname: string = currentHostname()): boolean {
  return hostname.endsWith(`.${TENANT_ROOT}`);
}

/** Synthetic tenant used for nexirt.com marketing routes. */
const INTERNAL_TENANT: TenantResolved = {
  tenantId: "nexirt-internal",
  name: "NEXIRT",
  branding: null,
  status: "active",
};

export async function resolveTenant(hostname: string = currentHostname()): Promise<TenantResolved> {
  if (!isTenantSubdomain(hostname)) return INTERNAL_TENANT;

  const cached = readCache();
  if (cached && cached.hostname === hostname) return cached.tenant;

  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/tenant/resolve?host=${encodeURIComponent(hostname)}`);
      if (!res.ok) return INTERNAL_TENANT;
      const tenant = (await res.json()) as TenantResolved;
      writeCache(hostname, tenant);
      return tenant;
    } catch {
      return INTERNAL_TENANT;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function readCache(): { hostname: string; tenant: TenantResolved } | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { hostname: string; tenant: TenantResolved };
  } catch {
    return null;
  }
}

function writeCache(hostname: string, tenant: TenantResolved) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ hostname, tenant }));
  } catch {
    // sessionStorage full / disabled — degrade silently.
  }
}

/**
 * Allowlist for tenant-supplied `customCSS`. Only these CSS variables
 * can be set by a tenant (prevents `position: fixed` injection +
 * layout-breaking values). Any key outside this set is dropped.
 */
const ALLOWED_CSS_VARS = new Set([
  "--nx-accent",
  "--nx-accent-2",
  "--nx-accent-soft",
  "--nx-text",
  "--nx-text-dim",
  "--nx-text-faint",
  "--nx-bg",
  "--nx-surface",
  "--nx-surface-2",
  "--nx-line",
  "--nx-line-strong",
]);

export function brandingCssVars(branding: TenantBranding | null): Record<string, string> {
  if (!branding) return {};
  const out: Record<string, string> = {};
  if (branding.accentColor) out["--nx-accent"] = sanitizeColor(branding.accentColor) ?? "";
  if (branding.accentColorSecondary) out["--nx-accent-2"] = sanitizeColor(branding.accentColorSecondary) ?? "";
  if (branding.customCSS) {
    for (const [k, v] of Object.entries(branding.customCSS)) {
      if (!ALLOWED_CSS_VARS.has(k)) continue;
      const safe = sanitizeColor(v);
      if (safe) out[k] = safe;
    }
  }
  return out;
}

/**
 * Narrow CSS color sanitizer. Accepts hex #RGB, #RRGGBB, or hsl(...)
 * / rgb(...) function forms; rejects anything with semicolons, braces,
 * angle brackets, url(), @-rules, or attr()/calc() expressions.
 */
function sanitizeColor(value: string): string | null {
  const v = value.trim();
  if (!v || v.length > 64) return null;
  if (/[;{}<>]/.test(v)) return null;
  if (/url\(|@|attr\(|calc\(|expression\(/i.test(v)) return null;
  if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
  if (/^(rgb|hsl)a?\([^)]+\)$/i.test(v)) return v;
  if (/^[a-z]+$/i.test(v)) return v; // named colors
  return null;
}
